import { supabase } from "@/integrations/supabase/client";
import { docHref, DocCollection } from "@/lib/sopLinks";

export type DocIndexEntry = {
  doc_id: string;
  title: string;
  short_title: string;
  collection: DocCollection;
  trigger_tags: string[];
  path: string;
  tokens: Set<string>; // lowercased title + trigger tag tokens
  aliases: Set<string>; // normalized link_aliases (deterministic matches)
};

/** Normalize a phrase to alias form: lowercase, hyphens→spaces, non-alnum stripped. */
export function normalizeAlias(input: string): string {
  return (input || "")
    .toLowerCase()
    .replace(/-/g, " ")
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

let cache: DocIndexEntry[] | null = null;
let byId: Map<string, DocIndexEntry> | null = null;
let pending: Promise<DocIndexEntry[]> | null = null;

const STOP = new Set([
  "the", "a", "an", "of", "and", "or", "to", "for", "on", "in", "at", "by",
  "with", "from", "as", "is", "it", "this", "that", "your", "you", "our",
  "into", "onto", "how", "what", "when", "why", "do", "does", "did", "i",
]);

function tokenize(s: string): string[] {
  return (s || "")
    .toLowerCase()
    .replace(/-/g, " ") // treat hyphens as spaces so "opening-accounts" == "opening accounts"
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 1 && !STOP.has(w));
}

export function shortenTitle(title: string): string {
  if (!title) return "";
  let t = title.trim();
  // Prefer first clause before colon, em/en dash, or question mark.
  const m = t.match(/^([^:—–?]+)[:—–?]/);
  if (m) t = m[1].trim();
  // Fall back to first sentence.
  const dot = t.match(/^([^.]{6,}?)\.\s/);
  if (dot) t = dot[1].trim();
  if (t.length > 70) t = t.slice(0, 67).trimEnd() + "…";
  return t;
}

function splitTags(tags: string | null | undefined): string[] {
  if (!tags) return [];
  return tags.split(";").map((t) => t.trim()).filter(Boolean);
}

export function loadDocIndex(): Promise<DocIndexEntry[]> {
  if (cache) return Promise.resolve(cache);
  if (!pending) {
    pending = (async () => {
      const { data } = await supabase
        .from("sop_documents" as any)
        .select("doc_id,title,collection,trigger_tags,slug,link_aliases")
        .eq("status", "published");
      const list: DocIndexEntry[] = [];
      for (const r of (data as any[]) || []) {
        const collection: DocCollection = r.collection === "faq" ? "faq" : "sop";
        const tags = splitTags(r.trigger_tags);
        const tokens = new Set<string>([
          ...tokenize(r.title || ""),
          ...tags.flatMap(tokenize),
        ]);
        const aliases = new Set<string>();
        for (const a of (r.link_aliases as string[] | null) || []) {
          const n = normalizeAlias(a);
          if (n) aliases.add(n);
        }
        // Belt-and-braces defaults so retitles are covered even if trigger hasn't fired.
        const nt = normalizeAlias(r.title || "");
        if (nt) aliases.add(nt);
        const ns = normalizeAlias((r.slug || "").replace(/-/g, " "));
        if (ns) aliases.add(ns);
        list.push({
          doc_id: r.doc_id,
          title: r.title || r.doc_id,
          short_title: shortenTitle(r.title || r.doc_id),
          collection,
          trigger_tags: tags,
          path: docHref({ doc_id: r.doc_id, collection, slug: r.slug }),
          tokens,
          aliases,
        });
      }
      cache = list;
      byId = new Map(list.map((e) => [e.doc_id, e]));
      return list;
    })();
  }
  return pending;
}

export function getDocById(id: string): DocIndexEntry | null {
  return byId?.get(id) || null;
}

/**
 * Resolve an inner phrase. Alias lookup FIRST (deterministic); token-fuzzy fallback.
 */
export function resolvePhrase(
  inner: string,
  index: DocIndexEntry[],
  selfDocId?: string,
): DocIndexEntry | null {
  // Deterministic alias pass.
  const norm = normalizeAlias(inner);
  if (norm) {
    const aliasHits = index.filter(
      (e) => (!selfDocId || e.doc_id !== selfDocId) && e.aliases.has(norm),
    );
    if (aliasHits.length === 1) return aliasHits[0];
    if (aliasHits.length > 1) {
      // Ambiguous alias — prefer exact title match if one of them matches perfectly.
      const titleHit = aliasHits.find((e) => normalizeAlias(e.title) === norm);
      if (titleHit) return titleHit;
      return null;
    }
  }

  // Fuzzy fallback.
  const toks = tokenize(inner);
  if (!toks.length) return null;
  const hits: { entry: DocIndexEntry; score: number }[] = [];
  for (const e of index) {
    if (selfDocId && e.doc_id === selfDocId) continue;
    let hit = 0;
    for (const t of toks) if (e.tokens.has(t)) hit++;
    if (hit === toks.length) {
      const titleLower = e.title.toLowerCase();
      const contiguous = toks.every((t) => titleLower.includes(t));
      hits.push({ entry: e, score: hit + (contiguous ? 0.5 : 0) });
    }
  }
  if (hits.length === 0) return null;
  hits.sort((a, b) => b.score - a.score);
  if (hits.length === 1) return hits[0].entry;
  if (hits[0].score > hits[1].score) return hits[0].entry;
  return null;
}

export function invalidateDocIndex() {
  cache = null;
  byId = null;
  pending = null;
}
